// ============================================================================
// File: CuCapacityPanel.tsx — Spec 215 US1 (T022)
// Author: Peter Dobler | Created: 2026-07-04
// Feature: 215-admin-dashboard-remediation
// Description: The CU depth panel that replaces the Collection Health tier table
//   in the Admin top row (owner directive: "use this space for more CU graphs
//   and stats"). Three visuals over /kpis.cuFleet:
//     (c) intraday 24h fleet utilization sparkline (hourly avg + max)
//     (a) per-capacity utilization table — CU% (null = "no data", never 0%),
//         SKU, paused state, throttling flag
//     (d) throttling timeline — which capacities crossed 80% and when (7 days)
// Constitution: v1.23.0 | Principle XXXVII, XLV (kit + tokens + recharts)
// ============================================================================
import React from 'react';
import { Link } from 'react-router-dom';
import { ComposedChart, Area, Line, XAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { DashboardPanel, Badge, Spinner, DataTable, chartTooltipStyle, ErrorState } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { formatEastern, formatEasternTime } from '@/utils/dateFormatter';
import type { DashboardKpis } from '../../../../../shared/types/dashboard';

interface CuCapacityPanelProps {
  data: DashboardKpis['cuFleet'] | undefined;
  loading?: boolean;
}

type CapRow = NonNullable<DashboardKpis['cuFleet']>['perCapacity'][number];

const hourLabel = (iso: string) => formatEasternTime(iso);

export const CuCapacityPanel: React.FC<CuCapacityPanelProps> = ({ data, loading = false }) => {
  const intraday = (data?.intraday24h ?? []).map((p) => ({ ...p, label: hourLabel(p.hourUtc) }));
  const capacities = data?.perCapacity ?? [];
  const timeline = data?.throttlingTimeline ?? [];

  // Timeline rollup: latest throttling hour + total hot readings per capacity (top 5).
  const byCapacity = new Map<string, { name: string; hours: number; readings: number; latest: string }>();
  for (const t of timeline) {
    const cur = byCapacity.get(t.capacityId);
    if (cur) {
      cur.hours += 1;
      cur.readings += t.readingsAbove80;
      if (t.hourUtc > cur.latest) cur.latest = t.hourUtc;
    } else {
      byCapacity.set(t.capacityId, { name: t.capacityName, hours: 1, readings: t.readingsAbove80, latest: t.hourUtc });
    }
  }
  const topThrottlers = Array.from(byCapacity.values()).sort((a, b) => b.readings - a.readings).slice(0, 5);

  const columns: DataTableColumn<CapRow>[] = [
    { key: 'capacityName', header: 'Capacity', cellClassName: 'font-semibold' },
    { key: 'sku', header: 'SKU', render: (c) => c.sku ?? '—' },
    {
      key: 'cuUsedPct',
      header: 'CU 24h',
      align: 'right',
      sortValue: (c) => c.cuUsedPct ?? -1,
      render: (c) =>
        c.cuUsedPct === null ? (
          // Edge case: no telemetry in the window ⇒ "no data", never a healthy-looking 0%.
          <span className="text-[var(--color-text-secondary)]">no data</span>
        ) : (
          <span className={
            c.cuUsedPct >= 80 ? 'font-bold text-[var(--color-status-danger)]'
            : c.cuUsedPct >= 60 ? 'font-bold text-[var(--color-status-warning)]'
            : 'text-[var(--color-text-primary)]'
          }>
            {c.cuUsedPct.toFixed(1)}%
          </span>
        ),
    },
    {
      key: 'state',
      header: 'State',
      render: (c) =>
        c.isPaused ? <Badge variant="default" size="sm">Paused</Badge>
        : c.throttling24h ? <Badge variant="error" size="sm">Throttling</Badge>
        : <Badge variant="success" size="sm">OK</Badge>,
    },
  ];

  return (
    <DashboardPanel
      title="CU by Capacity"
      titleInfo="Per-capacity CU utilization over the last 24h (hourly average + peak) with SKU and throttling state — the capacity-level breakdown behind the fleet number. 'no data' means no telemetry in the window, not 0%."
      actions={
        <Link to="/operations/telemetry?tab=live" className="text-xs font-medium text-[var(--color-accent)] hover:underline">
          Live telemetry →
        </Link>
      }
    >
      {loading ? (
        <div className="flex min-h-[100px] items-center justify-center"><Spinner size="md" /></div>
      ) : !data ? (
        // Spec 298 FR-005/FR-006: a section that was not read is said so, never shown as zeros
        <ErrorState title="Could not read the per-capacity CU figures" />
      ) : (
        <div className="flex h-full flex-col gap-2">
          {/* (c) intraday 24h fleet utilization */}
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)]">Last 24h · hourly avg + max</div>
            <div className="h-16">
              {intraday.length > 1 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={intraday} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
                    <defs>
                      <linearGradient id="cuIntraGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" hide />
                    <Tooltip
                      {...chartTooltipStyle}
                      formatter={((v: unknown, name: unknown) => [`${Number(v).toFixed(1)}%`, String(name)]) as any}
                      labelFormatter={(l: unknown) => String(l)}
                    />
                    <Area type="monotone" dataKey="avgPct" name="Avg" stroke="#6366f1" fill="url(#cuIntraGrad)" dot={false} strokeWidth={1.5} />
                    <Line type="monotone" dataKey="maxPct" name="Max" stroke="#94a3b8" strokeDasharray="4 3" fill="none" dot={false} strokeWidth={1.2} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-[var(--color-text-secondary)]">No telemetry in the last 24h</span>
              )}
            </div>
          </div>

          {/* (a) per-capacity utilization table — capacities with CU data first, A→Z within group (server-ordered) */}
          <DataTable
            columns={columns}
            rows={capacities.slice(0, 8)}
            rowKey={(c) => c.capacityId}
            dense
            emptyMessage="No active capacities"
          />

          {/* (d) throttling timeline — top offenders over the last 7 days */}
          <div data-testid="cu-throttling-timeline">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)]">Throttling · last 7 days</div>
            {topThrottlers.length > 0 ? (
              <div className="space-y-1">
                {topThrottlers.map((t) => (
                  <div key={t.name} className="flex items-center justify-between rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)] px-2 py-1">
                    <span className="truncate text-xs font-medium text-[var(--color-text-primary)]">{t.name}</span>
                    <span className="ml-2 shrink-0 text-[11px] text-[var(--color-text-secondary)]">
                      {t.readings} readings &gt;80% · {t.hours}h affected · last{' '}
                      {formatEastern(t.latest)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-[var(--color-text-secondary)]">No capacity crossed 80% in the last 7 days</div>
            )}
          </div>
        </div>
      )}
    </DashboardPanel>
  );
};

export default CuCapacityPanel;
